import React, { FC, useState } from 'react';
import { IconEdit, IconFlag, IconMapPin, IconTrash } from '../../icons';
import { FormattedMessage } from 'react-intl';
import EntityListItemERTooltip from './EntityListItemERTooltip';
import { useEntitySchema } from '../../hooks/useEntitySchema';
import ErrorReportDialog from '../ErrorReportDialog/ErrorReportDialog';
import Permissions from '../auth/Permissions';
import Tooltip from '../Tooltip';
import IconButton from '../core/IconButton';
import Skeleton from '../core/Skeleton';
import { cn } from '../../helpers/classnames';

interface EntityListItemProps {
  entity: Entity;
  errorReports?: ErrorReport[];
  className?: string;
  onRemoveClick?: () => void;
  onEditClick?: () => void;
  onLocationClick?: () => void;
  /** To disable default tooltip set it to empty string. */
  locationTooltip?: React.ReactNode;
  /** To disable default tooltip set it to empty string. */
  removeTooltip?: React.ReactNode;
  /** To disable default tooltip set it to empty string. */
  editTooltip?: React.ReactNode;
}

const EntityListItem: FC<EntityListItemProps> = ({
  entity,
  className,
  errorReports,
  onRemoveClick,
  onEditClick,
  onLocationClick,
  locationTooltip,
  removeTooltip,
  editTooltip,
}) => {
  const { data: schema } = useEntitySchema();
  const [showERDialog, setShowERDialog] = useState<boolean>(false);

  if (!schema) {
    return <Skeleton variant="block" className="h-[38px] w-full" />;
  }

  return (
    <>
      {showERDialog && errorReports && (
        <ErrorReportDialog
          open={showERDialog}
          entities={[entity]}
          onClose={() => setShowERDialog(false)}
          schema={schema}
          errorReports={errorReports}
        />
      )}
      <div
        className={cn(
          'flex min-h-[34px] w-full min-w-0 items-center gap-2 overflow-hidden',
          className,
        )}
      >
        {onLocationClick && (
          <Tooltip
            label={
              locationTooltip !== undefined ? (
                locationTooltip
              ) : (
                <FormattedMessage
                  id="entity_list_item.location_tooltip"
                  defaultMessage="Go to station details for {station_name}"
                  values={{
                    station_name: entity.properties['meta.name'] as string,
                  }}
                />
              )
            }
          >
            <IconButton
              variant="ghost"
              size="sm"
              onClick={onLocationClick}
              color="yellow"
              icon={<IconMapPin className="text-primary-500" />}
            />
          </Tooltip>
        )}
        {errorReports && errorReports.length > 0 && (
          <Tooltip
            label={
              <EntityListItemERTooltip
                schema={schema}
                errorReports={errorReports}
              />
            }
          >
            <div>
              <Permissions oneOf={['error-reports:create']}>
                <IconButton
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowERDialog(true)}
                  icon={<IconFlag className="text-red-500" />}
                />
              </Permissions>
              <Permissions noneOf={['error-reports:create']}>
                <IconFlag className="size-6 text-red-500" />
              </Permissions>
            </div>
          </Tooltip>
        )}
        <span className="min-w-0 shrink-0 truncate text-base font-semibold">
          {entity.full_name}
        </span>
        <span className="min-w-0 grow truncate text-text-800">
          {entity.properties['meta.id'] as string}
        </span>
        {entity.properties['meta.active'] === false && (
          <span className="min-w-0 shrink-0 truncate text-sm font-semibold text-text-800">
            <FormattedMessage id="inactive" defaultMessage="Inactive" />
          </span>
        )}
        {onEditClick && (
          <Tooltip
            label={
              editTooltip !== undefined ? (
                editTooltip
              ) : (
                <FormattedMessage
                  id="entity_list_item.edit_tooltip"
                  defaultMessage="Edit stop {stop_name}"
                  values={{
                    stop_name: entity.full_name,
                  }}
                />
              )
            }
          >
            <IconButton
              variant="ghost"
              size="sm"
              onClick={onEditClick}
              icon={<IconEdit />}
            />
          </Tooltip>
        )}
        {onRemoveClick && (
          <Tooltip
            label={
              removeTooltip !== undefined ? (
                removeTooltip
              ) : (
                <FormattedMessage
                  id="entity_list_item.remove_tooltip"
                  defaultMessage="Remove stop {stop_name} from list"
                  values={{
                    stop_name: entity.full_name,
                  }}
                />
              )
            }
          >
            <IconButton
              variant="ghost"
              size="sm"
              color="red"
              onClick={onRemoveClick}
              icon={<IconTrash className="text-red-500" />}
            />
          </Tooltip>
        )}
      </div>
    </>
  );
};

export default EntityListItem;
